"use client";

import { IconButton } from "@chakra-ui/react";
import type { SyntheticEvent } from "react";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import {
  getOriginActionBorderColor,
  getOriginActionColor,
  getOriginActionHoverBg,
} from "../config/originActionPresentation";
import type { ResolvedOriginAction } from "../hooks/useOriginItemActions";

type OriginActionIconButtonProps = {
  action: ResolvedOriginAction;
  isPrimary?: boolean;
  onActionSelect?: (action: ResolvedOriginAction) => boolean | void;
  onInteraction?: (event: SyntheticEvent) => void;
};

export const ORIGIN_ACTION_ICON_BUTTON_SIZE = "9";

export default function OriginActionIconButton({
  action,
  isPrimary = false,
  onActionSelect,
  onInteraction,
}: OriginActionIconButtonProps) {
  const isPrimaryAction = isPrimary || action.key === "extract";
  const actionColor = getOriginActionColor(action);
  const iconColor = isPrimary ? "white" : actionColor;

  return (
    <IconButton
      size="xs"
      justifyContent="center"
      alignItems="center"
      variant={isPrimary ? "solid" : "outline"}
      bg={
        isPrimary
          ? "moveon.primary"
          : isPrimaryAction
            ? "moveon.primaryLight"
            : undefined
      }
      color={iconColor}
      borderColor={getOriginActionBorderColor(action)}
      borderWidth="1"
      gap="0"
      minW={ORIGIN_ACTION_ICON_BUTTON_SIZE}
      w={ORIGIN_ACTION_ICON_BUTTON_SIZE}
      h="8"
      px="0"
      onClick={(event) => {
        onInteraction?.(event);

        if (onActionSelect?.(action)) {
          return;
        }

        void action.onClick();
      }}
      onPointerDown={onInteraction}
      onKeyDown={onInteraction}
      aria-label={action.humanReadable}
      title={action.humanReadable}
      _hover={{
        bg: isPrimary ? undefined : getOriginActionHoverBg(action),
      }}
      _focusVisible={{
        outlineWidth: "2px",
        outlineStyle: "solid",
        outlineColor: isPrimaryAction ? "moveon.primary" : actionColor,
        outlineOffset: "2px",
      }}
    >
      <GenericIcon
        icon={action.icon}
        size={iconSizePresets.inline}
        color={iconColor}
        humanReadable={action.humanReadable}
      />
    </IconButton>
  );
}
